import { BlockListSection, DetailSection, Resume } from '@sr/types';
import arrayMove from 'array-move';
import * as React from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { Box, Input } from '../../ui';
import API from '../../utils/api';
import AddSection from './AddSection';
import SidebarRow from './SidebarRow';
import SidebarSection from './SidebarSection';
import { Sentry } from 'utils/sentry';

type Props = {
  resume: Resume;
  closeDrawer?: () => void;
};

function moveSection(dragIndex: number, hoverIndex: number, resume: Resume) {
  const { sectionsOrder } = resume;
  if (sectionsOrder) {
    const newSectionsOrder = arrayMove(sectionsOrder, dragIndex, hoverIndex);
    API.resume.updateSectionsOrder({
      resumeId: resume.id,
      sectionsOrder: newSectionsOrder,
    });
  } else {
    Sentry.captureException(new Error('Missing resume.sectionsOrder'), {
      tags: {
        resumeId: resume.id,
      },
    });
  }
}

function renameResume(resume: Resume, name: string) {
  if (!name || name.length > 0) {
    API.resume.updateName({
      resumeId: resume.id,
      name,
    });
  }
}

function moveSectionItem(
  dragIndex: number,
  hoverIndex: number,
  section: DetailSection | BlockListSection
) {
  const { itemsOrder } = section;
  if (itemsOrder) {
    const newItemsOrder = arrayMove(itemsOrder, dragIndex, hoverIndex);
    API.section.update({
      section,
      updates: { itemsOrder: newItemsOrder },
    });
  } else {
    Sentry.captureException(new Error('Missing section.itemsOrder'), {
      tags: {
        sectionId: section.sectionId,
      },
    });
  }
}

const Sidebar = ({ resume, closeDrawer }: Props) => {
  const [sectionHovered, setSectionHovered] = React.useState(false);
  function onDragEnd(result: DropResult) {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    if (result.type === 'SECTION_ITEM') {
      const sectionId = result.source.droppableId;
      const section = resume.sections[sectionId] as
        | DetailSection
        | BlockListSection;
      if (!section) throw new Error('Section required to move item.');
      moveSectionItem(result.source.index, result.destination.index, section);
    } else if (result.type === 'SECTION') {
      moveSection(result.source.index, result.destination.index, resume);
    } else {
      throw new Error(
        `Tried to drag and item with unkown type: ${result.type}`
      );
    }
  }

  return (
    <Box
      id="gi-write-sidebar-sections-list"
      sx={{
        pb: 112,
        ':hover .dragSection': {
          opacity: sectionHovered ? 1 : undefined,
        },
      }}
    >
      <Input
        variant="inline"
        type="text"
        value={resume.name}
        sx={{
          fontSize: 'base',
          fontWeight: 'bold',
          color: 'black70',
          my: 3,
        }}
        onChange={(e) => renameResume(resume, e.currentTarget.value)}
        id="gi-write-resume-name"
      />
      <SidebarRow id="aboutid" title="About" closeSidebar={closeDrawer} />
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections" type="SECTION">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {(resume.sectionsOrder || []).map((sectionId, index) => {
                const section = resume.sections && resume.sections[sectionId];
                if (!section) return false;
                return (
                  <SidebarSection
                    resume={resume}
                    sectionId={section.sectionId}
                    title={section.title}
                    index={index}
                    key={section.sectionId}
                    closeSidebar={closeDrawer}
                    setSectionHovered={setSectionHovered}
                  />
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <AddSection resume={resume} closeDrawer={closeDrawer} />
    </Box>
  );
};

export default Sidebar;
